import cn from "classnames";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { chartActions } from "redux/modules/common/chart/actions";
import {
  chartProjectIdsSelector,
  chartTreeExpanderSelector,
  chartTreeFiltersSelector,
} from "redux/modules/common/chart/selectors";
import { askMoreChartProjects, toggleTreeOpen } from "redux/modules/common/chart/thunks";
import { IChartTree } from "redux/modules/common/chart/types";

import { ChartContext } from "../../Chart";
import ChartIntervalsRow from "../ChartIntervalsRow/ChartIntervalsRow";
import ChartTreeBubbles from "../ChartTreeBubbles/ChartTreeBubbles";
import { createPortal } from "react-dom";

import { CHART_TREE_LVL } from "../../constants";

import { displayDateFilter } from "../../utils/displayDateFilter";

import styles from "./ChartTree.module.scss";

export interface IChartTreeItemProps {
  data: IChartTree;
  style: any;
  isOpen: boolean;
  setOpen: (isOpen: boolean) => void;
}

const ChartTreeItem: React.FC<IChartTreeItemProps> = React.memo(({ data: tree, style, isOpen, setOpen }) => {
  const dispatch = useDispatch();
  const expander = useSelector(chartTreeExpanderSelector);
  const filters = useSelector(chartTreeFiltersSelector);
  const chartProjectIds = useSelector(chartProjectIdsSelector);

  const hasProjectLvlSkipFilter = filters?.skipBranchLvl?.[CHART_TREE_LVL.PROJECT];

  const clickHandler = useCallback(() => {
    setOpen(!isOpen);
    dispatch(toggleTreeOpen(tree._id, !isOpen));
    dispatch(chartActions.updateArrowHash());
  }, [tree._id, isOpen, setOpen]);

  const { containerRef } = useContext(ChartContext);
  const [container, setContainer] = useState(containerRef?.current);
  useEffect(() => {
    setContainer(containerRef?.current);
  }, [containerRef?.current]);

  const index = Math.ceil(style.top / style.height);

  useEffect(() => {
    if (
      tree.lvl !== CHART_TREE_LVL.PROJECT ||
      chartProjectIds.length < 3 ||
      tree.id != chartProjectIds[chartProjectIds.length - 2]
    )
      return;
    dispatch(askMoreChartProjects());
  }, []);

  if (!tree || index === 0 || (tree.lvl === CHART_TREE_LVL.PROJECT && hasProjectLvlSkipFilter)) return null;

  const isCheckboxVisible = isOpen !== undefined && tree.children.length > 0 && !filters?.skipBranchLvl?.[tree.lvl + 1];
  const isNumberVisible = !isCheckboxVisible && tree.lvl !== CHART_TREE_LVL.PROJECT && tree.number !== undefined;
  const intervalsBranchStyle = Object.assign({}, style, {
    left: "35%",
    // – style.height из-за rootNode, который всегда есть но не отображается
    top: `calc(${(style.top || 0) - ((hasProjectLvlSkipFilter ? 2 : 1) * style.height || 0)}px + 2.25rem)`,
    width: 0,
  });

  const branchElementStyle = Object.assign({}, style, {
    // – style.height из-за rootNode, который всегда есть но не отображается
    top: `${(style.top || 0) - ((hasProjectLvlSkipFilter ? 2 : 1) * style.height || 0)}px`,
  });

  return (
    <>
      <div
        style={branchElementStyle}
        className={cn(styles.branchElement, {
          [styles.bgLight]: index % 2 === 1,
        })}
        key={`branch_${tree._id}`}
      >
        <div
          className={cn(styles.branch, {
            [styles.branchChild]: tree.lvl === CHART_TREE_LVL.LSR,
            [styles.branchChildActive]: tree.lvl === CHART_TREE_LVL.LSR && tree.children.length > 0,
            [styles.branchExpenditure]: tree.lvl === CHART_TREE_LVL.SECTION,
            [styles.branchExpenditureChild]: tree.lvl === CHART_TREE_LVL.EXPENDITURE,
            [styles.branchExpenditureChildWithNumber]: isNumberVisible,
          })}
          onClick={clickHandler}
          data-chart-tree-uid={tree._id}
        >
          <div
            className={cn(styles.branchNameWrapper, {
              [styles.wDays]: expander?.days,
              [styles.wPeriod]: expander?.period,
            })}
          >
            {isCheckboxVisible && (
              <div
                className={cn(
                  styles.branchCheckbox,
                  {
                    [styles.branchCheckboxTrue]: !isOpen,
                  },
                  styles.branchCheckboxFalse
                )}
              />
            )}
            {isNumberVisible && (
              <span className={styles.branchNumber} title={tree.number}>
                {tree.number}
              </span>
            )}
            <div className={styles.branchName}>{tree.name}</div>
          </div>
          <div className={styles.branchBubbles}>
            <ChartTreeBubbles tree={tree} />
          </div>
          {expander && (
            <div className={styles.flexDaysPeriods}>
              {expander.days && (
                <div className={styles.days}>
                  <span>{tree.plan_duration || "-"}</span>
                </div>
              )}
              {expander.period && (
                <div className={styles.period}>
                  <span>
                    {Boolean(tree.plan_start && tree.plan_end)
                      ? `${displayDateFilter(tree.plan_start)} - ${displayDateFilter(tree.plan_end)}`
                      : "-"}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
        {container
          ? createPortal(
              <div style={intervalsBranchStyle} className={styles.intervalsBranch} key={`intervals_${tree._id}`}>
                <ChartIntervalsRow tree={tree} isOpen={isOpen} />
              </div>,
              container
            )
          : null}
      </div>
    </>
  );
});

export default React.memo(ChartTreeItem);
